import styled from "@emotion/styled";
import { IconDownload } from "@kompare/kompare-ui-library";
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useFunnelNavigate } from 'src/common/hooks/useFunnelNavigate.hook';
import {
  DownloadService, i18n, mediaQuery, setOfferId, setProgressBarPercentage, useAppDispatch, useAppSelector
} from 'src/common';
import { processQueryResult } from 'src/common/api/apollo';
import { useCheckoutHealthOfferQuery } from 'src/common/api/apollo/supplementary';
import { Card, Divider, Flex, Label, Loader, OfferHeaderCard, Text } from 'src/components';
import { colors } from 'src/theme';
import { AppRoutes } from '../../_router/app.routes';

const CardStyle2 = styled(Card)({
  padding: '16px 12px',
  [mediaQuery('mobile')]: {
    padding: '16px 20px',
  },
});

const TermsDocRowStyle = styled(Flex)({
  marginBottom: 16,
});

const TermsLinkStyle = styled.div({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  textDecoration: 'none',
  minWidth: 0,
});

const TermsLinkTextStyle = styled(Text)({
  marginLeft: 8,
  wordBreak: 'break-word',
  minWidth: 0,
});

const TermsAndConditionsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useFunnelNavigate();
  const params = useParams<{ offerId: string }>();
  const offerId = params.offerId as string;

  useEffect(() => {
    dispatch(setProgressBarPercentage(60));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setOfferId(offerId));
  }, [offerId, dispatch]);

  const soId = useAppSelector((store) => store.model.offer.soId);
  const getCheckoutHealthOfferQuery = useCheckoutHealthOfferQuery({
    variables: { offerId, soId: soId ?? '' },
    fetchPolicy: 'network-only',
    onCompleted: (result: any) => processQueryResult(result),
  });

  if (getCheckoutHealthOfferQuery.loading) return <Loader page />;
  if (getCheckoutHealthOfferQuery.error) {
    navigate(AppRoutes.CUSTOMER.fullPath);
    return null;
  }

  const { checkoutHealthOffer, checkoutHealthOfferData, getTermsAndConditionsFromFiles } = getCheckoutHealthOfferQuery.data || {};
  const termsDocuments = getTermsAndConditionsFromFiles?.documents ?? [];

  if (!checkoutHealthOfferData || !checkoutHealthOffer) return null;

  return (
    <>
      <Flex vertical gap={24}>
        <CardStyle2 radius={12}>
          <Flex vertical gap={16}>
            <OfferHeaderCard
              infoOnly
              logoUrl={checkoutHealthOffer.supplementary?.logoUrl ?? undefined}
              offerLabel={checkoutHealthOffer.supplementary?.label ?? undefined}
              brandName={checkoutHealthOffer.supplementary?.brandName ?? undefined}
            />
            <Divider dashed />
            {termsDocuments.length > 0 ? (
              <>
                <Label type="medium" style={{ marginBottom: 8 }}>
                  {i18n.t('pages.termsAndConditionsPage.documentsLabel')}
                </Label>
                {termsDocuments.map(
                  (doc: any, idx: number) =>
                    doc?.fileId &&
                    doc?.fileName && (
                      <TermsDocRowStyle key={idx} align="center">
                        <TermsLinkStyle
                          role="button"
                          tabIndex={0}
                          onClick={() => DownloadService.downloadFile(doc.fileId, `${doc.fileName}`)}
                          onKeyDown={(e) =>
                            e.key === 'Enter' && DownloadService.downloadFile(doc.fileId, `${doc.fileName}`)
                          }
                        >
                          <IconDownload />
                          <TermsLinkTextStyle color={colors.text3} type="base" bold>
                            {doc.fileName}
                          </TermsLinkTextStyle>
                        </TermsLinkStyle>
                      </TermsDocRowStyle>
                    )
                )}
              </>
            ) : (
              <Text type="base" color={colors.text4}>
                {i18n.t('pages.termsAndConditionsPage.noDocuments')}
              </Text>
            )}
          </Flex>
        </CardStyle2>
      </Flex>
    </>
  );
};

export default TermsAndConditionsPage;
